/**
 * Dev state-switcher fallback data. Used ONLY when import.meta.env.DEV
 * AND the dashboard's state switcher is set to a non-'auto' override.
 *
 * Real data flows from partnerApi.* hooks; these are scaffolding so the
 * page can be visually inspected in all four states (zero/warming/active/pro)
 * without an actual database backfill.
 */

import type { Period, SeriesPoint } from '@/lib/types';

export type PartnerState = 'zero' | 'warming' | 'active' | 'pro';

export interface HeroMock {
  /** Display key (e.g. 'today', 'yesterday', '7d', '30d'). */
  period: Period | 'today' | 'yesterday';
  /** Computed commission for the period (cents). */
  commissionCents: number;
  /** Commission for the previous comparable period (cents). */
  commissionCentsPrev: number;
  /** Conversions count in current period. */
  conversions: number;
  /** Clicks in current period. */
  clicks: number;
  /** Projection range `[low, high]` for end-of-period, cents. Only "today" period. */
  projectionLowCents?: number;
  projectionHighCents?: number;
  /** 0..1 confidence in the projection band. */
  projectionConfidence?: number;
  /** Last refresh, seconds ago. */
  refreshedAtSec?: number;
  /** Sparkline values for the period (e.g. 7 hourly buckets for today). */
  sparkline: number[];
}

export interface KpiMock {
  current: number;
  previous: number;
  sparkline: number[];
}

export interface OfferMock {
  id: string;
  name: string;
  payoutLabel: 'CPA' | 'RevShare' | 'Hybrid';
  geo: string;
  conversions: number;
  commissionCents: number;
  trend: number[];
  anomaly?: 'drop' | 'spike' | null;
  best?: boolean;
}

export interface ActivityMock {
  id: string;
  type: 'conversion' | 'click' | 'payout' | 'alert' | 'system';
  title: string;
  meta?: string;
  amountCents?: number;
  iso: string;
}

export interface HourCellMock {
  hour: number;
  commissionCents: number;
  conversions: number;
  clicks: number;
}

export interface WeekdayCellMock {
  /** 0..6, where 0 = Mon (RU-style: Пн..Вс). */
  dow: number;
  label: string;       // "Пн", "Вт"…
  todayCents: number;   // commission earned this dow in the current week
  lastWeekCents: number; // same dow last week
  isToday?: boolean;
}

export interface MilestoneMock {
  /** Display title — "До месячного рекорда", "До выплаты". */
  title: string;
  /** Cents earned so far toward the milestone. */
  currentCents: number;
  /** Target cents for the milestone. */
  targetCents: number;
  /** Optional ETA hint — "≈ 3д при текущем темпе". */
  etaLabel?: string;
}

export interface HoldReleaseMock {
  /** ISO date when this slice of hold opens. */
  iso: string;
  /** Cents that release on that date. */
  cents: number;
}

export interface EpcBandMock {
  /** Historical baseline EPC in cents (per day, 1d..N matches chart length). */
  baseline: number[];
  /** Lower 1-σ in cents. */
  low: number[];
  /** Upper 1-σ in cents. */
  high: number[];
}

export interface ForecastBandMock {
  /** Projected median commission per upcoming day, cents. */
  median: number[];
  low: number[];
  high: number[];
}

export interface PersonalBestMock {
  cents: number;
  iso: string;       // when the record was set
  todaySoFarCents: number; // for "rebeat the record" progress
}

export interface DashboardMock {
  state: PartnerState;
  hero: HeroMock;
  kpis: { clicks: KpiMock; leads: KpiMock; conversions: KpiMock; epcCents: KpiMock };
  chart: SeriesPoint[];
  wallet: { availableCents: number; holdCents: number; pendingCents: number; minPayoutCents: number };
  topOffers: OfferMock[];
  activity: ActivityMock[];
  sources: Array<{ label: string; share: number; count: number }>;
  geo: Array<{ country: string; flag: string; share: number; conversions: number }>;
  hourly: HourCellMock[]; // 24 cells, today
  /** Best hour label e.g. "14:00–15:00" (or null). */
  bestHour: string | null;
  /** Personality / narrative line shown under hero (algorithmic insight). */
  narrative: string | null;
  /** Period-comparable deltas (% signed) for the period segmented. */
  periodDelta: { '7d': number | null; '30d': number | null; '90d': number | null; '12m': number | null };
  /** Current-day pace forecast (cents). */
  paceCents: number | null;
  /** Daily revenue goal (cents) — fallback when partner hasn't set a preference. */
  defaultGoalCents: number;
  /** Weekday spine — last 7 weekdays vs same days last week. */
  weekdays: WeekdayCellMock[];
  /** Personal best record. */
  personalBest: PersonalBestMock | null;
  /** Milestone to motivate near-term progress. */
  milestone: MilestoneMock | null;
  /** Future hold release schedule (sorted by iso asc). */
  holdReleases: HoldReleaseMock[];
  /** EPC historical 1-σ band (parallel to chart points length, last N). */
  epcBand: EpcBandMock | null;
  /** Forecast confidence band for next 7 days (parallel array). */
  forecast: ForecastBandMock | null;
}

const NOW = Date.now();

/** Build a realistic 24-cell hourly distribution peaking around `peak` hour. */
function genHourly(peakHour = 14, scale = 1, variance = 0.25): HourCellMock[] {
  return Array.from({ length: 24 }, (_, h) => {
    const dist = Math.exp(-Math.pow(h - peakHour, 2) / 18);
    const noise = (Math.random() - 0.5) * variance;
    const base = Math.max(0, dist * scale + noise * scale);
    const conversions = Math.max(0, Math.round(base * 0.18));
    const clicks = Math.max(0, Math.round(base * 12));
    return {
      hour: h,
      commissionCents: Math.round(base * 4200),
      conversions,
      clicks,
    };
  });
}

function genSeries(days: number, scale: number, variance = 0.4): SeriesPoint[] {
  return Array.from({ length: days }, (_, i) => {
    const t = NOW - (days - 1 - i) * 86400_000;
    const base = scale + Math.sin(i / 3) * scale * 0.25 + i * scale * 0.02;
    const noise = (Math.random() - 0.5) * scale * variance;
    const clicks = Math.max(0, Math.round(base + noise));
    const leads = Math.round(clicks * 0.08);
    const conversions = Math.round(leads * 0.3);
    return {
      date: new Date(t).toISOString().slice(0, 10),
      clicks,
      leads,
      conversions,
      commissionCents: conversions * 4200 + Math.round(noise * 30),
    };
  });
}

const WD_LABELS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
function genWeekdays(scale: number, todayDow = new Date().getDay()): WeekdayCellMock[] {
  // Convert Sun..Sat (0..6) to Mon..Sun (0..6)
  const ruToday = (todayDow + 6) % 7;
  // Realistic affiliate pattern: weekdays high, weekend dips slightly. No zero days.
  const weights = [0.92, 1.00, 0.98, 1.10, 1.05, 0.78, 0.70];
  return WD_LABELS.map((label, i) => {
    const w = weights[i];
    const isToday = i === ruToday;
    const isFuture = i > ruToday;
    // Today's bar accumulates through the day — partial if it's literally today.
    const dayFraction = isToday ? Math.min(1, Math.max(0.15, new Date().getHours() / 24)) : 1;
    const today = isFuture
      ? 0
      : Math.max(Math.round(scale * 0.18), Math.round(scale * w * (0.85 + Math.random() * 0.3) * dayFraction));
    // Last week: always > 0 (we have data from a week ago for every weekday).
    const last = Math.max(Math.round(scale * 0.22), Math.round(scale * w * (0.80 + Math.random() * 0.35)));
    return { dow: i, label, todayCents: today, lastWeekCents: last, isToday };
  });
}

function genHoldReleases(baseDays: number[], amounts: number[]): HoldReleaseMock[] {
  const now = Date.now();
  return baseDays.map((d, i) => ({
    iso: new Date(now + d * 86_400_000).toISOString(),
    cents: amounts[i] ?? 0,
  }));
}

function genEpcBand(len: number, mid: number): EpcBandMock {
  const baseline = Array.from({ length: len }, () => mid);
  const low = baseline.map((v) => Math.round(v * 0.75));
  const high = baseline.map((v) => Math.round(v * 1.28));
  return { baseline, low, high };
}

function genForecast(lastVal: number, days = 7): ForecastBandMock {
  const median: number[] = [];
  const low: number[] = [];
  const high: number[] = [];
  for (let i = 1; i <= days; i++) {
    // Mild upward drift with realistic per-step noise so the line isn't a straight pipe.
    const drift = 1 + (i / days) * 0.12;
    const seasonal = 1 + Math.sin(i * 0.9) * 0.06;
    const m = Math.round(lastVal * drift * seasonal);
    median.push(m);
    // Widening confidence band over time (5% → 22%)
    const bandPct = 0.05 + (i / days) * 0.17;
    low.push(Math.round(m * (1 - bandPct)));
    high.push(Math.round(m * (1 + bandPct)));
  }
  return { median, low, high };
}

export const DASHBOARD_MOCKS: Record<PartnerState, DashboardMock> = {
  zero: {
    state: 'zero',
    hourly: [],
    bestHour: null,
    narrative: null,
    periodDelta: { '7d': null, '30d': null, '90d': null, '12m': null },
    paceCents: null,
    defaultGoalCents: 0,
    weekdays: [],
    personalBest: null,
    milestone: null,
    holdReleases: [],
    epcBand: null,
    forecast: null,
    hero: { period: 'today', commissionCents: 0, commissionCentsPrev: 0, conversions: 0, clicks: 0, sparkline: [] },
    kpis: {
      clicks: { current: 0, previous: 0, sparkline: [] },
      leads: { current: 0, previous: 0, sparkline: [] },
      conversions: { current: 0, previous: 0, sparkline: [] },
      epcCents: { current: 0, previous: 0, sparkline: [] },
    },
    chart: [],
    wallet: { availableCents: 0, holdCents: 0, pendingCents: 0, minPayoutCents: 5000 },
    topOffers: [],
    activity: [],
    sources: [],
    geo: [],
  },

  warming: {
    state: 'warming',
    hourly: genHourly(14, 0.6, 0.15).map((c) => ({ ...c, commissionCents: 0, conversions: 0 })),
    bestHour: null,
    narrative: 'Трафик идёт. Первая конверсия обычно через 2–6 часов.',
    periodDelta: { '7d': null, '30d': null, '90d': null, '12m': null },
    paceCents: null,
    defaultGoalCents: 0,
    weekdays: [],
    personalBest: null,
    milestone: null,
    holdReleases: [],
    epcBand: null,
    forecast: null,
    hero: {
      period: 'today',
      commissionCents: 0,
      commissionCentsPrev: 0,
      conversions: 0,
      clicks: 184,
      refreshedAtSec: 8,
      sparkline: [0, 12, 28, 41, 58, 110, 184],
    },
    kpis: {
      clicks: { current: 184, previous: 0, sparkline: [0, 12, 28, 41, 58, 110, 184] },
      leads: { current: 4, previous: 0, sparkline: [0, 0, 0, 1, 2, 2, 4] },
      conversions: { current: 0, previous: 0, sparkline: [0, 0, 0, 0, 0, 0, 0] },
      epcCents: { current: 0, previous: 0, sparkline: [0, 0, 0, 0, 0, 0, 0] },
    },
    chart: genSeries(7, 60, 0.35).map((p) => ({ ...p, conversions: 0, commissionCents: 0 })),
    wallet: { availableCents: 0, holdCents: 0, pendingCents: 0, minPayoutCents: 5000 },
    topOffers: [],
    activity: [
      { id: 'w1', type: 'click', title: '/main · RU · Telegram', iso: new Date(NOW - 5 * 60_000).toISOString() },
      { id: 'w2', type: 'click', title: '/main · RU · Telegram', iso: new Date(NOW - 14 * 60_000).toISOString() },
      { id: 'w3', type: 'system', title: 'Первый клик зафиксирован', iso: new Date(NOW - 4 * 3600_000).toISOString() },
    ],
    sources: [{ label: 'Telegram', share: 1, count: 184 }],
    geo: [],
  },

  active: {
    state: 'active',
    hourly: genHourly(14, 1, 0.25),
    bestHour: '14:00–15:00',
    narrative: 'Лучший день за 7 дней · 5-й день подряд выше плана',
    periodDelta: { '7d': 12.4, '30d': 18.6, '90d': 8.2, '12m': 42.1 },
    paceCents: 178_400,
    defaultGoalCents: 160_000, // $1,600 daily goal — slightly above current pace ($1,247)
    weekdays: genWeekdays(160_000),
    personalBest: {
      cents: 198_400 * 100,
      iso: new Date(Date.now() - 9 * 86_400_000).toISOString(),
      todaySoFarCents: 124_730,
    },
    milestone: {
      title: 'До месячного рекорда',
      currentCents: 1_840_000,
      targetCents: 2_200_000,
      etaLabel: '≈ 4д при текущем темпе',
    },
    holdReleases: genHoldReleases([2, 5, 9, 14, 21], [42_100, 88_400, 124_300, 96_200, 70_080]),
    epcBand: genEpcBand(30, 96),
    forecast: genForecast(135_000, 7),
    hero: {
      period: 'today',
      commissionCents: 124730,
      commissionCentsPrev: 105210,
      conversions: 23,
      clicks: 1247,
      projectionLowCents: 165000,
      projectionHighCents: 205000,
      projectionConfidence: 0.73,
      refreshedAtSec: 12,
      sparkline: [4200, 8800, 14600, 23400, 41200, 78900, 124730],
    },
    kpis: {
      clicks: { current: 1247, previous: 1056, sparkline: [820, 905, 880, 1010, 1180, 1056, 1247] },
      leads: { current: 78, previous: 64, sparkline: [48, 52, 58, 71, 69, 64, 78] },
      conversions: { current: 23, previous: 19, sparkline: [12, 14, 17, 21, 18, 19, 23] },
      epcCents: { current: 100, previous: 99, sparkline: [78, 82, 90, 94, 102, 99, 100] },
    },
    chart: genSeries(30, 1200, 0.3),
    wallet: { availableCents: 184230, holdCents: 421080, pendingCents: 67200, minPayoutCents: 5000 },
    topOffers: [
      { id: 'crypto-casino-ru', name: 'Crypto Casino RU', payoutLabel: 'RevShare', geo: 'RU', conversions: 11, commissionCents: 64200, trend: [3, 5, 8, 9, 7, 11], best: true },
      { id: 'forex-broker-eu', name: 'Forex Broker EU', payoutLabel: 'CPA', geo: 'EU', conversions: 6, commissionCents: 31800, trend: [4, 6, 5, 7, 6, 6] },
      { id: 'ai-trading-bot', name: 'AI Trading Bot', payoutLabel: 'CPA', geo: 'WW', conversions: 4, commissionCents: 19200, trend: [8, 7, 6, 5, 4, 4], anomaly: 'drop' },
      { id: 'invest-app-mx', name: 'Investment App MX', payoutLabel: 'CPA', geo: 'LATAM', conversions: 2, commissionCents: 9530, trend: [1, 1, 2, 1, 2, 2] },
    ],
    activity: [
      { id: 'a1', type: 'conversion', title: '/tg-promo · RU', amountCents: 5800, iso: new Date(NOW - 2 * 60_000).toISOString() },
      { id: 'a2', type: 'conversion', title: '/crypto-casino · RU', amountCents: 4200, iso: new Date(NOW - 9 * 60_000).toISOString() },
      { id: 'a3', type: 'alert', title: 'Линк /test-3 без конверсий', meta: '340 кликов за 24ч', iso: new Date(NOW - 41 * 60_000).toISOString() },
      { id: 'a4', type: 'conversion', title: '/forex-eu · DE', amountCents: 12400, iso: new Date(NOW - 78 * 60_000).toISOString() },
      { id: 'a5', type: 'payout', title: 'USDT-TRC20', amountCents: 75000, iso: new Date(NOW - 6 * 3600_000).toISOString() },
    ],
    sources: [
      { label: 'Telegram', share: 0.58, count: 723 },
      { label: 'Прямые', share: 0.21, count: 261 },
      { label: 'Блогеры', share: 0.14, count: 174 },
      { label: 'Другое', share: 0.07, count: 89 },
    ],
    geo: [
      { country: 'RU', flag: '🇷🇺', share: 0.78, conversions: 18 },
      { country: 'BR', flag: '🇧🇷', share: 0.14, conversions: 3 },
      { country: 'DE', flag: '🇩🇪', share: 0.08, conversions: 2 },
    ],
  },

  pro: {
    state: 'pro',
    hourly: genHourly(20, 1.6, 0.18),
    bestHour: '20:00–21:00',
    narrative: 'Месячный рекорд через ~$84k · текущий темп вдвое выше среднего',
    periodDelta: { '7d': 8.9, '30d': 14.2, '90d': 22.7, '12m': 61.3 },
    paceCents: 712_300,
    defaultGoalCents: 640_000, // $6,400 daily goal — slightly above current pace ($5,289)
    weekdays: genWeekdays(640_000),
    personalBest: {
      cents: 684_100 * 100,
      iso: new Date(Date.now() - 16 * 86_400_000).toISOString(),
      todaySoFarCents: 528_900,
    },
    milestone: {
      title: 'До месячного рекорда',
      currentCents: 12_400_000,
      targetCents: 15_000_000,
      etaLabel: '≈ 2д при текущем темпе',
    },
    holdReleases: genHoldReleases([1, 4, 7, 12, 20, 28], [180_000, 320_000, 540_000, 410_000, 480_000, 380_800]),
    epcBand: genEpcBand(30, 99),
    forecast: genForecast(540_000, 7),
    hero: {
      period: 'today',
      commissionCents: 528900,
      commissionCentsPrev: 478100,
      conversions: 87,
      clicks: 5230,
      projectionLowCents: 685000,
      projectionHighCents: 755000,
      projectionConfidence: 0.81,
      refreshedAtSec: 6,
      sparkline: [38000, 84000, 156000, 248000, 362000, 458000, 528900],
    },
    kpis: {
      clicks: { current: 5230, previous: 4810, sparkline: [3800, 4100, 4400, 4700, 4900, 4810, 5230] },
      leads: { current: 384, previous: 351, sparkline: [240, 268, 295, 310, 340, 351, 384] },
      conversions: { current: 87, previous: 79, sparkline: [62, 68, 71, 74, 81, 79, 87] },
      epcCents: { current: 101, previous: 99, sparkline: [88, 92, 95, 98, 102, 99, 101] },
    },
    chart: genSeries(30, 4800, 0.2),
    wallet: { availableCents: 1284230, holdCents: 2310800, pendingCents: 432100, minPayoutCents: 5000 },
    topOffers: [
      { id: 'crypto-casino-ru', name: 'Crypto Casino RU', payoutLabel: 'RevShare', geo: 'RU', conversions: 38, commissionCents: 248000, trend: [28, 32, 30, 34, 36, 38], best: true },
      { id: 'forex-broker-eu', name: 'Forex Broker EU', payoutLabel: 'CPA', geo: 'EU', conversions: 24, commissionCents: 144000, trend: [18, 19, 22, 21, 23, 24] },
      { id: 'sports-betting-br', name: 'Sports Betting BR', payoutLabel: 'Hybrid', geo: 'BR', conversions: 16, commissionCents: 96000, trend: [10, 12, 14, 13, 15, 16] },
      { id: 'ai-trading-bot', name: 'AI Trading Bot', payoutLabel: 'CPA', geo: 'WW', conversions: 9, commissionCents: 40900, trend: [14, 12, 11, 10, 9, 9], anomaly: 'drop' },
    ],
    activity: [
      { id: 'p1', type: 'conversion', title: '/crypto-casino · RU', amountCents: 12400, iso: new Date(NOW - 30_000).toISOString() },
      { id: 'p2', type: 'conversion', title: '/forex-eu · DE', amountCents: 8200, iso: new Date(NOW - 2 * 60_000).toISOString() },
      { id: 'p3', type: 'conversion', title: '/sports-br · BR', amountCents: 6800, iso: new Date(NOW - 5 * 60_000).toISOString() },
      { id: 'p4', type: 'payout', title: 'USDT-TRC20', amountCents: 480000, iso: new Date(NOW - 2 * 3600_000).toISOString() },
    ],
    sources: [
      { label: 'Telegram', share: 0.51, count: 2667 },
      { label: 'Блогеры', share: 0.23, count: 1203 },
      { label: 'Прямые', share: 0.18, count: 942 },
      { label: 'Другое', share: 0.08, count: 418 },
    ],
    geo: [
      { country: 'RU', flag: '🇷🇺', share: 0.42, conversions: 36 },
      { country: 'BR', flag: '🇧🇷', share: 0.21, conversions: 18 },
      { country: 'DE', flag: '🇩🇪', share: 0.16, conversions: 14 },
      { country: 'IN', flag: '🇮🇳', share: 0.12, conversions: 10 },
      { country: 'MX', flag: '🇲🇽', share: 0.09, conversions: 9 },
    ],
  },
};

export function classifyState(opts: {
  clicksAllTime: number;
  conversionsAllTime: number;
  earnings30dCents: number;
  conversions30d: number;
}): PartnerState {
  if (opts.clicksAllTime === 0) return 'zero';
  if (opts.conversionsAllTime === 0) return 'warming';
  // pro: ≥ $1k/day average over 30d OR ≥ 100 conversions in 30d
  const avgDaily = opts.earnings30dCents / 30;
  if (avgDaily >= 100_000 || opts.conversions30d >= 100) return 'pro';
  return 'active';
}
